import * as React from 'react';
import { Timeline, Empty, Spin, Modal, Table } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import withStyles from 'react-jss';
import { CssType } from 'src/theme/jss-types';
import { withTranslation } from 'react-i18next';
import { formatDateTime } from 'src/utils/utils';
import { getEventConfig } from '../Receivables.utils';

interface PayoutEvent {
  event_id: string;
  event_time: string;
  remarks?: string;
  creator_name: string;
  event_type: string;
  previous_data: Record<string, any>;
  current_data: Record<string, any>;
  consignment_reference_number?: string;
  changes?: Array<{
    charge_id: string;
    previous_value: any;
    previous_attachment?: string;
    previous_remarks?: string;
    current_value: any;
    current_attachment?: string;
    current_remarks?: string;
    [key: string]: any;
  }>;
}

interface EventsTimelineProps {
  events: PayoutEvent[];
  loading: boolean;
  error: string | null;
  classes: Record<string, string>;
  t: (key: string) => string;
}

const styles = (): CssType => ({
  container: {
    width: '30%',
    minWidth: '350px',
    height: '100%',
    backgroundColor: '#F5F5F5',
    borderRight: '1px solid #D9D9D9',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    padding: '16px 20px',
    fontWeight: 600,
    fontSize: '16px',
    color: '#262626',
    borderBottom: '1px solid #D9D9D9',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#FAFAFA',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px 16px',
  },
  timeline: {
    padding: '10px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
  },
  errorContainer: {
    padding: '20px',
    textAlign: 'center',
    color: '#F5222D',
  },
  errorTitle: {
    marginBottom: '8px',
    fontWeight: 600,
  },
  errorMessage: {
    fontSize: '12px',
    color: '#8C8C8C',
  },
  emptyContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
  },
  timelineItem: {
    padding: '2px',
    minHeight: 40,
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 300,
    width: '100%',
  },
  dateStatus: {
    fontSize: 'small',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: '8px',
  },
  eventType: {
    fontSize: '10px',
    lineHeight: '12px',
    padding: '4px 8px',
    borderRadius: '15px',
    textAlign: 'center',
    display: 'inline-block',
    fontWeight: 600,
  },
  user: {
    fontSize: 'small',
  },
  userRow: {
    display: 'flex',
  },
  userLabel: {
    fontWeight: 200,
  },
  referenceNumber: {
    fontSize: '11px',
    color: '#666',
    marginTop: '2px',
  },
  rejectReason: {
    fontWeight: 200,
    fontSize: 'smaller',
  },
  infoIcon: {
    cursor: 'pointer',
    fontSize: '14px',
    color: '#1976D2',
  },
  modalEmptyTitle: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '8px',
  },
  modalEmptySubtitle: {
    fontSize: '14px',
    color: '#999',
  },
  modalEmptyContainer: {
    padding: '20px',
    textAlign: 'center',
  },
});

const EventsTimeline: React.FC<EventsTimelineProps> = ({
  events,
  loading,
  error,
  classes,
  t,
}) => {
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [selectedEventData, setSelectedEventData] = React.useState<PayoutEvent | null>(null);

  const columns = [
    {
      title: t('Charge ID'),
      dataIndex: 'charge_id',
      key: 'charge_id',
      render: (text: string) => text || '-',
    },
    {
      title: t('Previous Value'),
      dataIndex: 'previous_value',
      key: 'previous_value',
      render: (text: string) => text || '-',
    },
    {
      title: t('Previous Attachment'),
      dataIndex: 'previous_attachment',
      key: 'previous_attachment',
      render: (text: string) => (
        text !== '-'
          ? <a href={text} target="_blank" rel="noopener noreferrer">{t('view')}</a>
          : '-'
      ),
    },
    {
      title: t('Previous Remarks'),
      dataIndex: 'previous_remarks',
      key: 'previous_remarks',
      render: (text: string) => text || '-',
    },
    {
      title: t('Updated Value'),
      dataIndex: 'current_value',
      key: 'current_value',
      render: (text: string) => text || '-',
    },
    {
      title: t('Updated Attachment'),
      dataIndex: 'current_attachment',
      key: 'current_attachment',
      render: (text: string) => (
        text !== '-'
          ? <a href={text} target="_blank" rel="noopener noreferrer">{t('view')}</a>
          : '-'
      ),
    },
    {
      title: t('Updated Remarks'),
      dataIndex: 'current_remarks',
      key: 'current_remarks',
      render: (text: string) => text || '-',
    },
  ];

  const formatCreatorName = (creatorName: string): string => {
    if (!creatorName) return 'USER';
    if (creatorName.toLowerCase() === 'system') {
      return 'SYSTEM';
    }
    const names = creatorName.split(',').map((n) => n.trim());
    return (names[0] || creatorName).toUpperCase();
  };

  const processEventChanges = (previousData: any, currentData: any) => {
    const previousPayout = previousData?.payout || [];
    const currentPayout = currentData?.payout || [];

    const allChargeIds = new Set([
      ...previousPayout.map((item) => item.chargeId),
      ...currentPayout.map((item) => item.chargeId),
    ].filter(Boolean));

    return Array.from(allChargeIds).map((chargeId) => {
      const previousItem = previousPayout.find((item) => item.chargeId === chargeId);
      const currentItem = currentPayout.find((item) => item.chargeId === chargeId);

      return {
        charge_id: chargeId,
        previous_value: previousItem?.payout?.toString() || '-',
        previous_attachment: previousItem?.attachment || '-',
        previous_remarks: previousItem?.remark || '-',
        current_value: currentItem?.payout?.toString() || '-',
        current_attachment: currentItem?.attachment || '-',
        current_remarks: currentItem?.remark || '-',
      };
    });
  };

  const handleInfoClick = (event: PayoutEvent) => {
    const changes = processEventChanges(event.previous_data, event.current_data);
    setSelectedEventData({ ...event, changes });
    setIsModalVisible(true);
  };

  const renderTimelineItem = (event: PayoutEvent) => {
    const eventConfig = getEventConfig(event.event_type?.toLowerCase());
    const formattedDateTime = formatDateTime(event.event_time, 'DD MMM YYYY, hh:mm A', '-');

    return (
      <Timeline.Item key={event.event_id} color="#2b68c4">
        <div className={classes.timelineItem}>
          <div className={classes.dateStatus}>
            <span>
              {formattedDateTime}
            </span>
            {eventConfig.showInfo && (
              <InfoCircleOutlined
                onClick={() => handleInfoClick(event)}
                className={classes.infoIcon}
              />
            )}
            <span
              className={classes.eventType}
              style={{ backgroundColor: eventConfig.backgroundColor, color: eventConfig.color }}
            >
              {eventConfig.label}
            </span>
          </div>

          <div className={classes.user}>
            <div className={classes.userRow}>
              <div className={classes.userLabel}>
                By&nbsp;
              </div>
              <div>
                {formatCreatorName(event.creator_name)}
              </div>
            </div>
            <div className={classes.rejectReason}>
              {event.event_type.toLowerCase() === 'reject_payout' && (event.remarks)}
            </div>
            {event.consignment_reference_number && (
              <div className={classes.referenceNumber}>
                #{event.consignment_reference_number}
              </div>
            )}
          </div>
        </div>
      </Timeline.Item>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className={classes.loadingContainer}>
          <Spin tip={t('Loading events...')} />
        </div>
      );
    }

    if (error) {
      return (
        <div className={classes.errorContainer}>
          <div className={classes.errorTitle}>
            {t('Failed to load events')}
          </div>
          <div className={classes.errorMessage}>
            {error}
          </div>
        </div>
      );
    }

    if (events.length === 0) {
      return (
        <div className={classes.emptyContainer}>
          <Empty
            description={t('No events to display')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      );
    }

    return (
      <Timeline
        className={classes.timeline}
        reverse={false}
      >
        {events.map(renderTimelineItem)}
      </Timeline>
    );
  };

  return (
    <div className={classes.container}>
      <style>
        {`
          .ant-timeline-item-tail {
            border-left: 2px dotted #2b68c4 !important;
          }
        `}
      </style>
      <div className={classes.header}>
        {t('Events')}
      </div>
      <div className={classes.content}>
        {renderContent()}
      </div>
      <Modal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={1000}
      >
        <Table
          columns={columns}
          dataSource={selectedEventData?.changes || []}
          pagination={false}
          rowKey="charge_id"
          locale={{
            emptyText: (
              <div className={classes.modalEmptyContainer}>
                <div className={classes.modalEmptyTitle}>
                  {t('No changes detected')}
                </div>
                <div className={classes.modalEmptySubtitle}>
                  {t('This event did not modify any data that requires approval')}
                </div>
              </div>
            ),
          }}
        />
      </Modal>
    </div>
  );
};

const StyledEventsTimeline = withTranslation('translation')(EventsTimeline);
export default withStyles(styles, { injectTheme: true })(StyledEventsTimeline);
